import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, Package, LogOut, Edit, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { supabase } from '../lib/supabase';

export default function AccountPage() {
  const { user, profile, isAuthenticated, signOut, updateProfile } = useAuth();
  const navigate = useNavigate();

  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });

  useEffect(() => {
    setFormData({
      firstName: profile?.first_name || '',
      lastName: profile?.last_name || '',
      email: user?.email || '',
      phone: profile?.phone || '',
    });
  }, [profile, user]);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (!user?.id) return;

    const loadOrders = async () => {
      try {
        setOrdersLoading(true);

        const { data, error } = await supabase
          .from('orders')
          .select(`*, order_items (*)`)
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (error) throw error;
        setOrders(data || []);
      } catch (error) {
        console.error('Erreur chargement commandes:', error);
        toast.error('Impossible de charger vos commandes');
        setOrders([]);
      } finally {
        setOrdersLoading(false);
      }
    };

    loadOrders();
  }, [isAuthenticated, user?.id]);

  if (!isAuthenticated) {
    navigate('/connexion', { state: { from: { pathname: '/mon-compte' } } });
    return null;
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const result = await updateProfile({
        first_name: formData.firstName,
        last_name: formData.lastName,
        phone: formData.phone,
      });

      if (result.success) {
        toast.success('Profil mis à jour');
        setIsEditing(false);
      } else {
        toast.error(result.error || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      toast.error('Une erreur est survenue');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    toast.success('Déconnexion réussie');
    navigate('/');
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'SUPPRIMER') {
      toast.error('Veuillez taper SUPPRIMER pour confirmer');
      return;
    }

    setIsDeleting(true);

    try {
      const { data, error } = await supabase.functions.invoke('delete-account')

      if (error) throw error

      if (!data?.success) {
        toast.error(data?.message || 'Erreur lors de la suppression')
        return
      }

      await signOut()
      toast.success('Votre compte a été supprimé')
      navigate('/')
    } catch (error) {
      console.error('Erreur suppression compte:', error)
      toast.error('Une erreur est survenue lors de la suppression')
    } finally {
      setIsDeleting(false)
      setDeleteModalOpen(false)
    }
  }

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-CH', {
      style: 'currency',
      currency: 'CHF',
    }).format(Number(price || 0));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-CH', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'En attente',
      confirmed: 'Confirmée',
      preparing: 'En préparation',
      ready: 'Prête',
      completed: 'Terminée',
      cancelled: 'Annulée',
      processing: 'En préparation',
    };
    return labels[status] || status || 'En attente';
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      preparing: 'bg-indigo-100 text-indigo-800',
      ready: 'bg-purple-100 text-purple-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      processing: 'bg-blue-100 text-blue-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <>
      <main data-testid="account-page" className="min-h-screen pt-20 bg-warm-alabaster">
        <section className="bg-warm-secondary py-12 lg:py-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <h1 className="font-serif text-3xl lg:text-4xl text-espresso font-semibold">
              Mon Compte
            </h1>
            <p className="text-soft-charcoal-secondary mt-2">
              Bienvenue, {profile?.first_name || user?.email || 'client'} !
            </p>
          </div>
        </section>

        <section className="py-12 lg:py-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <Tabs defaultValue="profile" className="space-y-8">
              <TabsList className="bg-transparent border-b border-border-subtle w-full justify-start rounded-none h-auto p-0 space-x-8">
                <TabsTrigger
                  value="profile"
                  className="bg-transparent rounded-none border-b-2 border-transparent data-[state=active]:border-latte-gold data-[state=active]:bg-transparent pb-4 px-0"
                  data-testid="tab-profile"
                >
                  <User className="w-4 h-4 mr-2" />
                  Profil
                </TabsTrigger>
                <TabsTrigger
                  value="orders"
                  className="bg-transparent rounded-none border-b-2 border-transparent data-[state=active]:border-latte-gold data-[state=active]:bg-transparent pb-4 px-0"
                  data-testid="tab-orders"
                >
                  <Package className="w-4 h-4 mr-2" />
                  Commandes
                </TabsTrigger>
              </TabsList>

              <TabsContent value="profile">
                <div className="max-w-2xl">
                  <div className="bg-white p-6 lg:p-8 border border-border-subtle">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="font-serif text-xl font-semibold text-espresso">
                        Mes Informations
                      </h2>
                      {!isEditing && (
                        <Button
                          variant="ghost"
                          onClick={() => setIsEditing(true)}
                          className="text-latte-gold hover:text-latte-gold-secondary"
                          data-testid="edit-profile-btn"
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Modifier
                        </Button>
                      )}
                    </div>

                    {isEditing ? (
                      <form onSubmit={handleUpdateProfile} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="firstName">Prénom</Label>
                            <Input
                              id="firstName"
                              name="firstName"
                              value={formData.firstName}
                              onChange={handleInputChange}
                              className="input-field"
                              data-testid="profile-firstname"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="lastName">Nom</Label>
                            <Input
                              id="lastName"
                              name="lastName"
                              value={formData.lastName}
                              onChange={handleInputChange}
                              className="input-field"
                              data-testid="profile-lastname"
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="email">Email</Label>
                          <Input
                            id="email"
                            name="email"
                            value={formData.email}
                            disabled
                            className="input-field bg-warm-secondary"
                          />
                          <p className="text-xs text-soft-charcoal-secondary">
                            L'email ne peut pas être modifié
                          </p>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="phone">Téléphone</Label>
                          <Input
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            className="input-field"
                            data-testid="profile-phone"
                          />
                        </div>

                        <div className="flex gap-4 pt-4">
                          <Button
                            type="submit"
                            disabled={isLoading}
                            className="btn-primary"
                            data-testid="save-profile-btn"
                          >
                            {isLoading ? 'Enregistrement...' : 'Enregistrer'}
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            onClick={() => {
                              setFormData({
                                firstName: profile?.first_name || '',
                                lastName: profile?.last_name || '',
                                email: user?.email || '',
                                phone: profile?.phone || '',
                              });
                              setIsEditing(false);
                            }}
                            className="btn-secondary"
                          >
                            Annuler
                          </Button>
                        </div>
                      </form>
                    ) : (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-soft-charcoal-secondary text-sm">Prénom</p>
                            <p className="font-medium text-espresso">{profile?.first_name || '-'}</p>
                          </div>
                          <div>
                            <p className="text-soft-charcoal-secondary text-sm">Nom</p>
                            <p className="font-medium text-espresso">{profile?.last_name || '-'}</p>
                          </div>
                        </div>
                        <div>
                          <p className="text-soft-charcoal-secondary text-sm">Email</p>
                          <p className="font-medium text-espresso">{user?.email || '-'}</p>
                        </div>
                        <div>
                          <p className="text-soft-charcoal-secondary text-sm">Téléphone</p>
                          <p className="font-medium text-espresso">{profile?.phone || '-'}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Actions compte */}
                  <div className="mt-8 pt-8 border-t border-border-subtle flex items-center justify-between">
                    <Button
                      variant="ghost"
                      onClick={handleSignOut}
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      data-testid="logout-btn"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Se Déconnecter
                    </Button>

                    <Button
                      variant="ghost"
                      onClick={() => {
                        setDeleteConfirmText('')
                        setDeleteModalOpen(true)
                      }}
                      className="text-red-400 hover:text-red-600 hover:bg-red-50 text-sm"
                      data-testid="delete-account-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Supprimer mon compte
                    </Button>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="orders">
                <div className="space-y-6">
                  <h2 className="font-serif text-xl font-semibold text-espresso">
                    Historique des Commandes
                  </h2>

                  {ordersLoading ? (
                    <div className="bg-white p-8 border border-border-subtle text-center">
                      <p className="text-soft-charcoal-secondary">Chargement des commandes...</p>
                    </div>
                  ) : orders.length === 0 ? (
                    <div className="bg-white p-8 border border-border-subtle text-center">
                      <Package className="w-12 h-12 mx-auto mb-4 text-soft-charcoal-secondary" />
                      <h3 className="font-serif text-lg font-semibold text-espresso mb-2">
                        Aucune commande
                      </h3>
                      <p className="text-soft-charcoal-secondary mb-6">
                        Vous n'avez pas encore passé de commande.
                      </p>
                      <Button asChild className="btn-primary">
                        <Link to="/menu">Voir le Menu</Link>
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {orders.map((order) => (
                        <div
                          key={order.id}
                          className="bg-white p-6 border border-border-subtle"
                          data-testid={`order-${order.id}`}
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                            <div>
                              <p className="font-serif font-semibold text-espresso">
                                Commande {order.id}
                              </p>
                              <p className="text-soft-charcoal-secondary text-sm">
                                {formatDate(order.created_at)}
                              </p>
                            </div>
                            <div className="flex items-center gap-4">
                              <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(order.status)}`}>
                                {getStatusLabel(order.status)}
                              </span>
                              <span className="font-serif font-semibold text-espresso">
                                {formatPrice(order.total_amount)}
                              </span>
                              <Button asChild variant="ghost" className="btn-secondary">
                                <Link to={`/commande/${order.id}`}>Voir la commande</Link>
                              </Button>
                            </div>
                          </div>

                          <div className="border-t border-border-subtle pt-4">
                            {order.order_items?.length ? (
                              <ul className="space-y-2">
                                {order.order_items.map((item) => (
                                  <li key={item.id} className="flex justify-between text-sm">
                                    <span className="text-soft-charcoal">
                                      {item.product_name} × {item.quantity}
                                    </span>
                                    <span className="text-soft-charcoal-secondary">
                                      {formatPrice(item.total_price)}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p className="text-sm text-soft-charcoal-secondary">
                                Détails de commande indisponibles.
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </section>
      </main>

      {/* Modal suppression compte */}
      <Dialog open={deleteModalOpen} onOpenChange={(open) => {
        if (!isDeleting) setDeleteModalOpen(open)
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">
              Supprimer mon compte
            </DialogTitle>
            <DialogDescription className="space-y-3 pt-2">
              <p>
                Cette action est <strong>irréversible</strong>. Votre compte et vos données personnelles seront définitivement supprimés.
              </p>
              <p className="text-sm">
                Vos commandes passées seront conservées de manière anonyme pour les besoins de gestion.
              </p>
              <p className="text-sm font-medium text-espresso pt-2">
                Tapez <span className="font-mono bg-red-50 text-red-600 px-1 rounded">SUPPRIMER</span> pour confirmer :
              </p>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="SUPPRIMER"
                className="input-field font-mono"
                disabled={isDeleting}
              />
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => setDeleteModalOpen(false)}
              disabled={isDeleting}
              className="btn-secondary"
            >
              Annuler
            </Button>
            <Button
              onClick={handleDeleteAccount}
              disabled={isDeleting || deleteConfirmText !== 'SUPPRIMER'}
              className="bg-red-600 hover:bg-red-700 text-white border-none"
            >
              {isDeleting ? 'Suppression...' : 'Supprimer définitivement'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}